import { useState, useRef, useEffect } from 'react';
import {
  RecaptchaVerifier,
  signInWithPhoneNumber,
  getAdditionalUserInfo,
  type ConfirmationResult,
} from 'firebase/auth';
import { Phone } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { auth } from '@/shared/lib/firebase';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { LoadingButton } from './LoadingButton';
import { OtpInput } from './OtpInput';

const OTP_COOLDOWN_SECONDS = 60;

function isValidInternationalPhone(phone: string): boolean {
  return /^\+[1-9]\d{6,14}$/.test(phone.replace(/\s/g, ''));
}

interface PhoneAuthFlowProps {
  submitLabel: string;
  onSuccess: (isNewUser: boolean, needsProfileCompletion: boolean) => void;
  onError: (message: string) => void;
}

export function PhoneAuthFlow({ submitLabel, onSuccess, onError }: PhoneAuthFlowProps) {
  const { t } = useTranslation();
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [step, setStep] = useState<'phone' | 'otp'>('phone');
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const recaptchaContainerRef = useRef<HTMLDivElement>(null);
  const confirmationRef = useRef<ConfirmationResult | null>(null);
  const verifierRef = useRef<RecaptchaVerifier | null>(null);

  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setInterval(() => setCooldown((c) => c - 1), 1000);
    return () => clearInterval(id);
  }, [cooldown]);

  const sendOtp = async () => {
    if (cooldown > 0) return;

    const normalized = phone.replace(/\s/g, '');
    if (!isValidInternationalPhone(normalized)) {
      onError(t('auth.phone.errors.invalidPhone'));
      return;
    }

    setLoading(true);
    try {
      verifierRef.current?.clear();
      if (!recaptchaContainerRef.current) return;
      const verifier = new RecaptchaVerifier(auth, recaptchaContainerRef.current, { size: 'invisible' });
      verifierRef.current = verifier;
      confirmationRef.current = await signInWithPhoneNumber(auth, normalized, verifier);
      setCooldown(OTP_COOLDOWN_SECONDS);
      setStep('otp');
    } catch (err: unknown) {
      verifierRef.current?.clear();
      const code = (err as { code?: string })?.code ?? '';
      onError(
        code === 'auth/invalid-phone-number'
          ? t('auth.phone.errors.invalidFormat')
          : code === 'auth/too-many-requests'
          ? t('auth.phone.errors.tooManyRequests')
          : t('auth.phone.errors.sendFailed'),
      );
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async () => {
    if (!confirmationRef.current || otp.length < 6) return;
    setLoading(true);
    try {
      const result = await confirmationRef.current.confirm(otp);
      const info = getAdditionalUserInfo(result);
      const needsProfileCompletion = !result.user.providerData.some(
        (p) => p.providerId === 'password',
      );
      onSuccess(info?.isNewUser ?? false, needsProfileCompletion);
    } catch (err: unknown) {
      const code = (err as { code?: string })?.code ?? '';
      onError(
        code === 'auth/invalid-verification-code'
          ? t('auth.phone.errors.invalidCode')
          : code === 'auth/code-expired'
          ? t('auth.phone.errors.codeExpired')
          : t('auth.phone.errors.verifyFailed'),
      );
      setOtp('');
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setStep('phone');
    setOtp('');
    // Don't reset cooldown — prevents abuse by cycling back and forth
  };

  return (
    <div className="space-y-4">
      <div ref={recaptchaContainerRef} />

      {step === 'phone' ? (
        <div className="space-y-3">
          <div className="space-y-2">
            <Label htmlFor="phone-number">{t('auth.phone.phoneNumber')}</Label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
              <Input
                id="phone-number"
                type="tel"
                placeholder="+49 176 1234 5678"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && sendOtp()}
                className="h-12 pl-9 input-elegant"
                autoComplete="tel"
              />
            </div>
            <p className="text-xs text-muted-foreground">{t('auth.phone.hint')}</p>
          </div>
          <LoadingButton
            type="button"
            isLoading={loading}
            loadingText={t('auth.phone.sending')}
            onClick={sendOtp}
            disabled={phone.trim().length < 6 || loading || cooldown > 0}
          >
            {cooldown > 0
              ? t('auth.phone.resendIn', { seconds: cooldown })
              : t('auth.phone.sendCode')}
          </LoadingButton>
        </div>
      ) : (
        <div className="space-y-3">
          <OtpInput
            label={t('auth.phone.codeLabel', { phone })}
            value={otp}
            onChange={setOtp}
          />
          <LoadingButton
            type="button"
            isLoading={loading}
            loadingText={t('auth.phone.verifying')}
            onClick={verifyOtp}
            disabled={otp.length < 6 || loading}
          >
            {submitLabel}
          </LoadingButton>
          <button
            type="button"
            className="w-full text-xs disabled:opacity-40 disabled:cursor-not-allowed"
            onClick={reset}
            disabled={cooldown > 0}
          >
            {cooldown > 0
              ? <span className="text-muted-foreground">{t('auth.phone.resendAvailableIn', { seconds: cooldown })}</span>
              : <span className="text-accent hover:underline">{t('auth.phone.changeNumber')}</span>}
          </button>
        </div>
      )}
    </div>
  );
}
